import React, { createContext, useState, useContext, useEffect } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { lightTheme, darkTheme } from "./theme";

// Create Theme Context
const ThemeContext = createContext({
  mode: "light",
  themeMode: "light", // The user's preference: "light", "dark", or "system"
  toggleColorMode: () => {},
  setThemeMode: () => {},
  setThemeModeState: () => {}, // Internal function for user data sync
  setUserSaveFunction: () => {}, // Function to set the Firestore save function
});

// Custom hook to use theme context
export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
};

// Helper to detect system preference
const getSystemPreference = () => {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
};

// Helper to resolve actual mode from theme preference
const resolveMode = (themeMode) => {
  if (themeMode === "system") {
    return getSystemPreference();
  }
  return themeMode;
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // State for user's theme preference and actual resolved mode
  const [themeMode, setThemeModeState] = useState(() => {
    // Try to get from localStorage first as fallback
    const savedMode = localStorage.getItem("themeMode");
    return savedMode || "light";
  });

  const [mode, setMode] = useState(() => resolveMode(themeMode));
  const [userSaveFunction, setUserSaveFunction] = useState(null);

  // Update actual mode when theme preference or system preference changes
  useEffect(() => {
    const newMode = resolveMode(themeMode);
    setMode(newMode);

    // Also update localStorage for fallback
    localStorage.setItem("themeMode", themeMode);
  }, [themeMode]);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (themeMode !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      setMode(getSystemPreference());
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeMode]);

  // Function to update theme preference
  const setThemeMode = async (newThemeMode) => {
    console.log("📱 Setting theme mode to:", newThemeMode);

    // If we have a user save function (Firestore), use that
    if (userSaveFunction) {
      console.log("📱 Using Firestore save function");
      try {
        await userSaveFunction(newThemeMode);
        console.log("📱 Firestore save completed");
      } catch (error) {
        console.error(
          "📱 Firestore save failed, falling back to local:",
          error,
        );
        setThemeModeState(newThemeMode);
      }
    } else {
      // No user logged in, just set locally
      console.log("📱 No user save function, setting locally");
      setThemeModeState(newThemeMode);
    }
  };

  // Legacy toggle function for backwards compatibility
  const toggleColorMode = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setThemeMode(newMode);
  };

  const theme = mode === "light" ? lightTheme : darkTheme;

  const value = {
    mode,
    themeMode,
    toggleColorMode,
    setThemeMode,
    setThemeModeState, // Internal function for user data sync
    setUserSaveFunction, // Function to set the Firestore save function
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {/* enableColorScheme writes `color-scheme: dark` onto :root in dark
            mode — native browser chrome (scrollbars, form controls) follows
            the theme instead of staying light. */}
        <CssBaseline enableColorScheme />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
